import {
  Alert,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDebouncedValue } from '@mantine/hooks';
import { useState } from 'react';
import { useLocation } from 'wouter';
import { IntlPhoneInput } from '@/components/IntlPhoneInput';
import Link from '@/components/Link';
import {
  BUSINESS_MODELS,
  type DuplicateMatch,
  LEAD_SOURCES,
  type LeadClient,
  type LeadSource,
  useCreateLead,
  useDuplicateCheck,
} from '@/models/leads';

export function CreateLeadModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const [, setLocation] = useLocation();
  const mutate = useCreateLead();
  const [added, setAdded] = useState<LeadClient | null>(null);
  const close = () => {
    setAdded(null);
    onClose();
  };
  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      whatsapp: '',
      telegram: '',
      source: 'Manual' as LeadSource | null,
      sourceDetail: '',
      request: '',
      extendedMessage: '',
      website: '',
      businessModel: null as string | null,
    },
    validate: {
      firstName: (v, values) =>
        !v && !values.lastName ? 'First or last name required' : null,
      lastName: (v, values) =>
        !v && !values.firstName ? 'First or last name required' : null,
      source: (v) => (!v ? 'Source is required' : null),
    },
  });

  const [debounced] = useDebouncedValue(
    {
      email: form.values.email.trim(),
      phone: form.values.phone.trim(),
      firstName: form.values.firstName.trim(),
      lastName: form.values.lastName.trim(),
    },
    400,
  );
  const dup = useDuplicateCheck(debounced);
  const exact = dup.data?.exact ?? [];
  const similar = dup.data?.similar ?? [];
  const target = dup.data?.target ?? null;
  const clientName = (c: LeadClient) =>
    c.visible ? (
      <Link to={`~/contacts/${c.id}`} onClick={close}>
        {c.name}
      </Link>
    ) : (
      <Text span fw={500}>
        {c.name}
      </Text>
    );

  const submit = form.onSubmit(async (values) => {
    if (!values.email && !values.phone) {
      form.setFieldError('email', 'Email or phone required');
      form.setFieldError('phone', 'Email or phone required');
      return;
    }
    const { lead, contact, existing } = await mutate.mutateAsync({
      ...values,
      source: values.source!,
    });
    if (contact && existing) {
      form.reset();
      setAdded(contact);
      return;
    }
    onClose();
    setLocation(`~/leads/${lead.id}`);
  });

  const formatMatch = (m: DuplicateMatch, i: number) => {
    if (m.hidden || m.id == null) {
      return (
        <Text key={`hidden-${m.entity}-${i}`} size="sm" c="dimmed">
          {m.entity === 'lead' ? 'A request' : 'A person'} with these details
          exists{m.ownerName ? ` (owner: ${m.ownerName})` : ''}
        </Text>
      );
    }
    const name =
      [m.firstName, m.lastName].filter(Boolean).join(' ') || '(no name)';
    const contact =
      [m.email, m.phone].filter(Boolean).join(' — ') || '(no contact)';
    const label = `${m.entity === 'lead' ? 'Lead' : 'Contact'} #${m.id} — ${name} — ${contact}`;
    const to = m.entity === 'lead' ? `~/leads/${m.id}` : `~/contacts/${m.id}`;
    return (
      <Text key={`${m.entity}-${m.id}`} size="sm">
        <Link to={to} onClick={onClose}>
          {label}
        </Link>
      </Text>
    );
  };

  return (
    <Modal opened={opened} onClose={close} title="New Contact" size="lg">
      {added ? (
        <Stack>
          <Alert color="green" title="Request added to an existing client">
            <Text size="sm">
              The email belongs to client {clientName(added)}. The request was
              added to this client.
            </Text>
          </Alert>
          <Group justify="flex-end">
            <Button onClick={close}>Close</Button>
          </Group>
        </Stack>
      ) : (
        <form onSubmit={submit}>
          <Stack>
            <Group grow>
              <TextInput
                label="First name"
                {...form.getInputProps('firstName')}
              />
              <TextInput
                label="Last name"
                {...form.getInputProps('lastName')}
              />
            </Group>
            <Group grow>
              <TextInput
                label="Email"
                type="email"
                {...form.getInputProps('email')}
              />
              <IntlPhoneInput
                label="Phone"
                value={form.values.phone}
                onChange={(v) => form.setFieldValue('phone', v)}
                error={form.errors.phone as string}
              />
            </Group>
            <Group grow>
              <IntlPhoneInput
                label="WhatsApp"
                value={form.values.whatsapp}
                onChange={(v) => form.setFieldValue('whatsapp', v)}
                error={form.errors.whatsapp as string}
              />
              <TextInput label="Telegram" {...form.getInputProps('telegram')} />
            </Group>
            <Group grow>
              <Select
                label="Source"
                data={LEAD_SOURCES}
                searchable
                {...form.getInputProps('source')}
              />
              <TextInput
                label="Source detail"
                {...form.getInputProps('sourceDetail')}
              />
            </Group>
            <Group grow>
              <TextInput label="Website" {...form.getInputProps('website')} />
              <Select
                label="Business model"
                data={BUSINESS_MODELS}
                clearable
                {...form.getInputProps('businessModel')}
              />
            </Group>
            <Textarea
              label="Original request"
              autosize
              minRows={3}
              {...form.getInputProps('request')}
            />
            <Textarea
              label="Notes"
              autosize
              minRows={2}
              {...form.getInputProps('extendedMessage')}
            />
            {target && (
              <Alert color="blue" title="Existing client">
                <Text size="sm">
                  This email belongs to client {clientName(target)}. The request
                  will be added to this client.
                </Text>
              </Alert>
            )}
            {exact.length > 0 && (
              <Alert
                color="orange"
                title="Same phone or email found - a new person is created and the possible duplicate is queued for review"
              >
                {exact.map(formatMatch)}
              </Alert>
            )}
            {exact.length === 0 && similar.length > 0 && (
              <Alert color="yellow" title="Possible duplicates">
                {similar.map(formatMatch)}
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" loading={mutate.isPending}>
                Create
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
